import { useEffect, useMemo, useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import { useIntl } from 'react-intl'
import type { SortingRule } from 'react-table'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import { fetchTemplatesListThunk, templatesListActions } from './slice'
import { CommonSearch, MobileSortBy } from 'components/Table'
import { userAnyTopDomainIdSelector } from 'features/auth/permissions/selectors'
import {
  templatesListErrorSelector,
  templatesListIsFetchingSelector,
  templatesListIsFinishedSelector,
  templatesListSelector,
} from './selectors'
import { H4 } from 'components/Typography'
import SpinnerCentered from 'components/Loader/SpinnerCentered'
import TemplatesTable from './components/TemplatesTable'
import TemplateUploadButton from './components/TemplateUploadButton'
import { getSortOptions } from 'components/Table/utils'
import { TEMPLATES_HEADERS } from './consts'
import type { Template } from './types'
import { commonMessages } from 'app/i18n/messages'
import { templatesPageMessages } from 'features/templates/messages'
import { CurrentUserPermissionsContext } from 'features/auth/permissions/CurrentUserPermissionsContext'
import { ROOT_ADMIN } from 'features/auth/permissions/consts'
import usePermissions from 'features/auth/permissions/usePermissions'
import {
  StyledNoTemplates,
  StyledTemplatesContainer,
  StyledTemplatesHeader,
  TemplatesSearchWrapper,
} from './styles'

function Templates() {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()
  const [hasRootAdminPermission] = usePermissions({ allow: [ROOT_ADMIN] })

  const [searchValue, setSearchValue] = useState<string>()

  const userAnyTopDomainId = useAppSelector(userAnyTopDomainIdSelector)
  const templatesList = useAppSelector<Template[]>(templatesListSelector)
  const isFetching = useAppSelector(templatesListIsFetchingSelector)
  const isFinished = useAppSelector(templatesListIsFinishedSelector)
  const error = useAppSelector(templatesListErrorSelector)

  useEffect(() => {
    if (userAnyTopDomainId) {
      dispatch(fetchTemplatesListThunk(userAnyTopDomainId))
    }

    return () => {
      dispatch(templatesListActions.clear())
    }
  }, [dispatch, userAnyTopDomainId])

  const headers = useMemo(
    () => ({
      name: {
        label: formatMessage(templatesPageMessages.nameColumn),
        id: TEMPLATES_HEADERS.NAME,
      },
      uploaded: {
        label: formatMessage(templatesPageMessages.uploadedColumn),
        id: TEMPLATES_HEADERS.UPLOADED,
      },
      integratorName: {
        label: formatMessage(templatesPageMessages.uploadedByColumn),
        id: TEMPLATES_HEADERS.INTEGRATOR_NAME,
      },
      version: {
        label: formatMessage(templatesPageMessages.versionColumn),
        id: TEMPLATES_HEADERS.VERSION,
      },
      downloadLink: {
        label: '',
        id: TEMPLATES_HEADERS.DOWNLOAD_LINK,
      },
      DeleteButton: {
        label: '',
        id: TEMPLATES_HEADERS.ID,
      },
    }),
    [formatMessage]
  )

  const sortOptions = useMemo(
    () =>
      getSortOptions(headers).filter(
        (option) =>
          ![TEMPLATES_HEADERS.ID, TEMPLATES_HEADERS.DOWNLOAD_LINK].includes(
            option.id as TEMPLATES_HEADERS
          )
      ),
    [headers]
  )
  const [sortBy, setSortBy] = useState<SortingRule<Template>>(sortOptions[0])

  return (
    <StyledTemplatesContainer>
      <StyledTemplatesHeader>
        <H4>{formatMessage(templatesPageMessages.title)}</H4>
        {hasRootAdminPermission && <TemplateUploadButton />}
      </StyledTemplatesHeader>

      <TemplatesSearchWrapper>
        <CommonSearch
          setSearchValue={setSearchValue}
          placeholder={formatMessage(templatesPageMessages.searchPlaceholder)}
        />
      </TemplatesSearchWrapper>
      <MobileSortBy<Template>
        headers={headers}
        sortOptions={sortOptions}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />
      {isFetching && <SpinnerCentered />}
      {error && (
        <StyledNoTemplates>
          {formatMessage(commonMessages.commonError)}
        </StyledNoTemplates>
      )}
      {isFinished && isEmpty(templatesList) && (
        <StyledNoTemplates>
          {formatMessage(templatesPageMessages.noTemplatesFoundMessage)}
        </StyledNoTemplates>
      )}
      {isFinished && !isEmpty(templatesList) && (
        <CurrentUserPermissionsContext.Provider
          value={{ hasRootAdminPermission }}
        >
          <TemplatesTable
            data={templatesList}
            searchValue={searchValue}
            headers={headers}
            defaultSortBy={sortOptions[0]}
            sortBy={sortBy}
            setSortBy={setSortBy}
          />
        </CurrentUserPermissionsContext.Provider>
      )}
    </StyledTemplatesContainer>
  )
}

export default Templates
